import { Link, TextField, Typography } from '@mui/material';
import { AuBankAccountElement } from '@stripe/react-stripe-js';

import { elementStyle, StripeField } from '@/features/payments';

import type { BankTransferDetails } from './submitPayment';
import type { MethodCompletion } from './useMethodCompletion';

const BECS_AGREEMENT_URL = 'https://stripe.com/au-becs-dd-service-agreement/legal';

interface BankTransferFieldsProps {
  details: BankTransferDetails;
  onChange: (details: BankTransferDetails) => void;
  completion: MethodCompletion;
  disabled: boolean;
}

/**
 * BECS direct debit details.
 *
 * The email is not optional and not a convenience: Stripe sends the direct
 * debit confirmation to it, and BECS requires the payer be notified. The
 * mandate wording below is the agreement the director enters into by paying, so
 * it sits above the button rather than behind a link.
 */
export const BankTransferFields = ({
  details,
  onChange,
  completion,
  disabled,
}: BankTransferFieldsProps) => (
  <div className="flex flex-col gap-3">
    <TextField
      label="Account name"
      size="small"
      value={details.name}
      onChange={(event) => {
        onChange({ ...details, name: event.target.value });
      }}
      autoComplete="name"
      placeholder="As it appears on the account"
      disabled={disabled}
      slotProps={{ inputLabel: { shrink: true } }}
    />

    <TextField
      label="Email"
      size="small"
      type="email"
      inputMode="email"
      value={details.email}
      onChange={(event) => {
        onChange({ ...details, email: event.target.value });
      }}
      autoComplete="email"
      placeholder="you@example.com"
      helperText="Your direct debit confirmation is sent here."
      disabled={disabled}
      slotProps={{ inputLabel: { shrink: true } }}
    />

    <StripeField label="Bank account" htmlFor="auBankAccount">
      {(handlers) => (
        <AuBankAccountElement
          id="auBankAccount"
          options={{ style: elementStyle, disabled }}
          onChange={(event) => {
            completion.onBankAccountChange(event.complete);
          }}
          {...handlers}
        />
      )}
    </StripeField>

    <Typography variant="caption" color="text.secondary">
      By confirming this payment, you are setting up a direct debit from your bank account. You
      agree to the{' '}
      <Link href={BECS_AGREEMENT_URL} target="_blank" rel="noreferrer">
        Direct Debit service agreement
      </Link>
      , and authorise Stripe Payments Australia Pty Ltd (ACN 160 180 343) to debit your account
      through the Bulk Electronic Clearing System (BECS). You certify that you are an account
      holder or an authorised signatory on the account above.
    </Typography>
  </div>
);
